<template>
  <div class="container-fluid py-4">
    <!-- Header Card -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card shadow-sm border-0 border-radius-xl">
          <div class="card-header pb-0 bg-transparent d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h6 class="font-weight-bolder mb-0">Tenant & Company Management</h6>
              <p class="text-xs text-secondary mb-0">Manage system tenants, company credentials, assigned roles, and installation types</p>
            </div>
            <button class="btn bg-gradient-primary btn-sm mb-0 d-flex align-items-center gap-2" @click="openModal()">
              <i class="fas fa-plus"></i> Add New Tenant
            </button>
          </div>

          <!-- Alert Messages -->
          <div class="px-4 pt-3" v-if="errorMessage || successMessage">
            <div v-if="errorMessage" class="alert alert-danger text-white text-xs py-2 px-3 alert-dismissible fade show" role="alert">
              {{ errorMessage }}
              <button type="button" class="btn-close text-xs py-2 px-3" @click="errorMessage = ''"></button>
            </div>
            <div v-if="successMessage" class="alert alert-success text-white text-xs py-2 px-3 alert-dismissible fade show" role="alert">
              {{ successMessage }}
              <button type="button" class="btn-close text-xs py-2 px-3" @click="successMessage = ''"></button>
            </div>
          </div>

          <!-- Search Bar -->
          <div class="card-body px-4 pt-3 pb-2">
            <div class="row mb-3">
              <div class="col-md-4 col-12">
                <div class="input-group input-group-sm">
                  <span class="input-group-text"><i class="fas fa-search"></i></span>
                  <input type="text" v-model="searchQuery" class="form-control" placeholder="Search by company, email, username..." />
                </div>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">ID</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Company Name</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Short Name</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Username</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Email</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Mobile Number</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">ROLE</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Installation Type</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Address</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isLoading">
                    <td colspan="10" class="text-center py-4">
                      <div class="spinner-border spinner-border-sm text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <span class="ms-2 text-xs font-weight-bold text-secondary">Loading tenants...</span>
                    </td>
                  </tr>
                  <tr v-else-if="filteredTenants.length === 0">
                    <td colspan="10" class="text-center py-4 text-xs font-weight-bold text-secondary">
                      No tenants found.
                    </td>
                  </tr>
                  <tr v-for="tenant in filteredTenants" :key="tenant.id">
                    <td class="ps-3"><p class="text-xs font-weight-bold mb-0">#{{ tenant.id }}</p></td>
                    <td>
                      <div class="d-flex px-2 py-1 align-items-center">
                        <div class="avatar avatar-sm me-3 bg-gradient-dark border-radius-md d-flex align-items-center justify-content-center">
                          <span class="text-white text-xs font-weight-bold">{{ getInitials(tenant.company_name || tenant.name) }}</span>
                        </div>
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ tenant.company_name || tenant.name }}</h6>
                        </div>
                      </div>
                    </td>
                    <td><span class="badge bg-gradient-secondary text-xxs">{{ tenant.company_short_name || tenant.short_name || tenant.code || '-' }}</span></td>
                    <td><p class="text-xs font-weight-bold mb-0">{{ tenant.username || tenant.user_name || '-' }}</p></td>
                    <td><p class="text-xs font-weight-bold mb-0">{{ tenant.email || tenant.email_address || '-' }}</p></td>
                    <td><p class="text-xs font-weight-bold mb-0">{{ getMobileNumber(tenant) }}</p></td>
                    <td>
                      <span class="badge bg-gradient-dark text-xxs">
                        {{ getRoleDisplayName(tenant.role || tenant.role_name || tenant.role_id) }}
                      </span>
                    </td>
                    <td>
                      <span class="badge bg-gradient-info text-xxs">
                        {{ getInstallationTypeName(tenant.installation_type_id || tenant.installation_id || tenant.type_id) }}
                      </span>
                    </td>
                    <td><p class="text-xs text-secondary mb-0 text-truncate" style="max-width: 150px;">{{ tenant.address || '-' }}</p></td>
                    <td class="align-middle text-center">
                      <button class="btn btn-link text-info text-gradient px-2 mb-0" @click="editTenant(tenant)">
                        <i class="fas fa-pencil-alt me-1"></i> Edit
                      </button>
                      <button class="btn btn-link text-danger text-gradient px-2 mb-0" @click="deleteTenant(tenant.id)">
                        <i class="far fa-trash-alt me-1"></i> Delete
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop & Modal -->
    <div v-if="showModal" class="modal-backdrop fade show"></div>
    <div v-if="showModal" class="modal fade show d-block" tabindex="-1" role="dialog" style="background: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content shadow-lg border-0 border-radius-xl">
          <div class="modal-header bg-gradient-primary text-white border-0 py-3 px-4">
            <h5 class="modal-title text-white font-weight-bolder">{{ editMode ? 'Edit Tenant' : 'Add New Tenant' }}</h5>
            <button type="button" class="btn-close btn-close-white" @click="closeModal()"></button>
          </div>
          <div class="modal-body p-4">
            <form @submit.prevent="saveTenant">
              <div class="row g-3">
                <!-- Company Name -->
                <div class="col-md-6">
                  <label class="form-label text-xs font-weight-bold text-uppercase">Company Name <span class="text-danger">*</span></label>
                  <input
                    v-model="formData.company_name"
                    type="text"
                    class="form-control"
                    placeholder="Enter company name"
                    required
                  />
                </div>

                <!-- Company Short Name -->
                <div class="col-md-6">
                  <label class="form-label text-xs font-weight-bold text-uppercase">Company Short Name <span class="text-danger">*</span></label>
                  <input
                    v-model="formData.company_short_name"
                    type="text"
                    class="form-control"
                    placeholder="e.g. ACME"
                    required
                  />
                </div>

                <!-- Username -->
                <div class="col-md-6">
                  <label class="form-label text-xs font-weight-bold text-uppercase">Admin Username <span class="text-danger">*</span></label>
                  <input
                    v-model="formData.username"
                    type="text"
                    class="form-control"
                    placeholder="Enter admin username"
                    required
                  />
                </div>

                <!-- Email Address -->
                <div class="col-md-6">
                  <label class="form-label text-xs font-weight-bold text-uppercase">Email Address <span class="text-danger">*</span></label>
                  <input
                    v-model="formData.email"
                    type="email"
                    class="form-control"
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <!-- Password -->
                <div class="col-md-6">
                  <label class="form-label text-xs font-weight-bold text-uppercase">
                    Admin Password <span v-if="!editMode" class="text-danger">*</span>
                    <small v-else class="text-muted font-weight-normal">(Leave blank to keep unchanged)</small>
                  </label>
                  <input
                    v-model="formData.password"
                    type="password"
                    class="form-control"
                    placeholder="Enter password"
                    :required="!editMode"
                  />
                </div>

                <!-- Mobile Number -->
                <div class="col-md-6">
                  <label class="form-label text-xs font-weight-bold text-uppercase">Mobile Number <span class="text-danger">*</span></label>
                  <input
                    v-model="formData.mobile_number"
                    type="tel"
                    class="form-control"
                    placeholder="Enter mobile number"
                    required
                  />
                </div>

                <!-- Role Dropdown (Restricted to Admin roles only) -->
                <div class="col-md-6">
                  <label class="form-label text-xs font-weight-bold text-uppercase">Role <span class="text-danger">*</span></label>
                  <select
                    v-model="formData.role"
                    class="form-control"
                    required
                  >
                    <option value="Admin">Admin</option>
                    <option v-for="r in adminRolesList" :key="getRoleId(r)" :value="getRoleValue(r)">
                      {{ getRoleLabel(r) }}
                    </option>
                  </select>
                </div>

                <!-- Installation Type ID -->
                <div class="col-md-6">
                  <label class="form-label text-xs font-weight-bold text-uppercase">Installation Type <span class="text-danger">*</span></label>
                  <select
                    v-model="formData.installation_type_id"
                    class="form-control"
                    required
                  >
                    <option value="" disabled>Select Installation Type</option>
                    <option v-for="type in installationTypes" :key="type.id" :value="type.id">
                      {{ type.name || type.installation_type_name || ('Type #' + type.id) }}
                    </option>
                  </select>
                </div>

                <!-- Address -->
                <div class="col-12">
                  <label class="form-label text-xs font-weight-bold text-uppercase">Address</label>
                  <textarea
                    v-model="formData.address"
                    class="form-control"
                    rows="3"
                    placeholder="Enter company address"
                  ></textarea>
                </div>
              </div>

              <div class="d-flex justify-content-end gap-2 mt-4">
                <button type="button" class="btn btn-secondary btn-sm" @click="closeModal()">Cancel</button>
                <button type="submit" class="btn btn-primary btn-sm" :disabled="isSaving">
                  <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
                  {{ editMode ? 'Update Tenant' : 'Create Tenant' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "TenantsTable",
  data() {
    return {
      tenants: [],
      installationTypes: [],
      rolesList: [],
      searchQuery: "",
      showModal: false,
      editMode: false,
      isLoading: false,
      isSaving: false,
      errorMessage: "",
      successMessage: "",
      formData: {
        id: null,
        company_name: "",
        company_short_name: "",
        username: "",
        email: "",
        password: "",
        mobile_number: "",
        role: "Admin",
        role_id: "",
        address: "",
        installation_type_id: ""
      }
    };
  },
  computed: {
    apiUrl() {
      return process.env.VUE_APP_API_BASE_URL || "https://digitalsignage.digisamhita.com/api";
    },
    adminRolesList() {
      if (!Array.isArray(this.rolesList)) return [];
      return this.rolesList.filter(r => {
        const name = String(typeof r === 'object' && r !== null ? (r.role_name || r.name || r.title || '') : r).toLowerCase();
        return name.includes('admin');
      });
    },
    filteredTenants() {
      if (!this.searchQuery) return this.tenants;
      const q = this.searchQuery.toLowerCase();
      return this.tenants.filter(t => {
        const comp = String(t.company_name || t.name || '').toLowerCase();
        const shortComp = String(t.company_short_name || t.short_name || t.code || '').toLowerCase();
        const uname = String(t.username || t.user_name || '').toLowerCase();
        const email = String(t.email || t.email_address || '').toLowerCase();
        const mob = String(t.mobile_number || t.mobile || t.phone || t.phone_number || t.contact || '');
        return comp.includes(q) || shortComp.includes(q) || uname.includes(q) || email.includes(q) || mob.includes(q);
      });
    }
  },
  mounted() {
    this.fetchTenants();
    this.fetchInstallationTypes();
    this.fetchRoles();
  },
  methods: {
    getAuthHeaders() {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
      return { Authorization: `Bearer ${token}` };
    },
    getInitials(name) {
      if (!name) return 'T';
      return name.charAt(0).toUpperCase();
    },
    getMobileNumber(tenant) {
      if (!tenant) return '-';
      return tenant.mobile_number || tenant.mobile || tenant.phone || tenant.phone_number || tenant.contact || '-';
    },
    getInstallationTypeName(typeId) {
      const found = this.installationTypes.find(t => String(t.id) === String(typeId));
      return found ? (found.name || found.installation_type_name || ('Type #' + found.id)) : (typeId || 'N/A');
    },
    getRoleId(r) {
      return typeof r === 'object' && r !== null ? r.id || r.role_id : r;
    },
    getRoleValue(r) {
      return typeof r === 'object' && r !== null ? (r.role_name || r.name || r.title || r.id) : r;
    },
    getRoleLabel(r) {
      return typeof r === 'object' && r !== null ? (r.role_name || r.name || r.title || ('Role #' + r.id)) : r;
    },
    getRoleDisplayName(val) {
      if (!val) return 'Admin';
      if (typeof val === 'object' && val !== null) {
        return val.role_name || val.name || val.title || 'Admin';
      }
      return val;
    },
    async fetchInstallationTypes() {
      try {
        const res = await axios.get(`${this.apiUrl}/v1/admin/installation-types`, {
          headers: this.getAuthHeaders()
        });
        this.installationTypes = res.data?.data || res.data?.types || res.data || [];
      } catch (err) {
        console.error("Error fetching installation types:", err);
      }
    },
    async fetchRoles() {
      try {
        let res;
        try {
          res = await axios.get(`${this.apiUrl}/v1/admin/tenant/roles`, { headers: this.getAuthHeaders() });
        } catch (e) {
          res = await axios.get(`${this.apiUrl}/v1/admin/roles`, { headers: this.getAuthHeaders() });
        }
        this.rolesList = res.data?.data || res.data?.roles || res.data || [];
      } catch (err) {
        console.error("Error fetching roles:", err);
        this.rolesList = [];
      }
    },
    async fetchTenants() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        let res;
        try {
          res = await axios.get(`${this.apiUrl}/v1/admin/tenants`, { headers: this.getAuthHeaders() });
        } catch (e) {
          res = await axios.get(`${this.apiUrl}/v1/admin/tenant`, { headers: this.getAuthHeaders() });
        }
        this.tenants = res.data?.data || res.data?.tenants || res.data || [];
      } catch (err) {
        console.error("Error fetching tenants:", err);
        this.tenants = this.tenants || [];
      } finally {
        this.isLoading = false;
      }
    },
    openModal(tenant = null) {
      if (tenant) {
        this.editTenant(tenant);
      } else {
        this.errorMessage = "";
        this.successMessage = "";
        this.editMode = false;
        this.formData = {
          id: null,
          company_name: "",
          company_short_name: "",
          username: "",
          email: "",
          password: "",
          mobile_number: "",
          role: "Admin",
          role_id: "",
          address: "",
          installation_type_id: ""
        };
        this.showModal = true;
      }
    },
    editTenant(tenant) {
      this.errorMessage = "";
      this.successMessage = "";
      this.editMode = true;
      this.formData = {
        id: tenant.id,
        company_name: tenant.company_name || tenant.name || "",
        company_short_name: tenant.company_short_name || tenant.short_name || tenant.code || "",
        username: tenant.username || tenant.user_name || "",
        email: tenant.email || tenant.email_address || "",
        password: "",
        mobile_number: tenant.mobile_number || tenant.mobile || tenant.phone || tenant.phone_number || tenant.contact || "",
        role: tenant.role || tenant.role_name || "Admin",
        role_id: tenant.role_id || tenant.role || "",
        address: tenant.address || "",
        installation_type_id: tenant.installation_type_id || tenant.installation_id || tenant.type_id || ""
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveTenant() {
      this.isSaving = true;
      this.errorMessage = "";
      this.successMessage = "";

      const payload = {
        company_name: this.formData.company_name,
        company_short_name: this.formData.company_short_name,
        username: this.formData.username,
        email: this.formData.email,
        mobile_number: this.formData.mobile_number,
        mobile: this.formData.mobile_number,
        phone: this.formData.mobile_number,
        role: this.formData.role,
        role_id: this.formData.role_id || this.formData.role,
        address: this.formData.address,
        installation_type_id: this.formData.installation_type_id
      };

      if (this.formData.password) {
        payload.password = this.formData.password;
      }

      try {
        if (this.editMode) {
          try {
            await axios.put(`${this.apiUrl}/v1/admin/tenants/${this.formData.id}`, payload, { headers: this.getAuthHeaders() });
          } catch (e) {
            await axios.post(`${this.apiUrl}/v1/admin/tenant/update/${this.formData.id}`, payload, { headers: this.getAuthHeaders() });
          }
          this.successMessage = "Tenant updated successfully!";
        } else {
          try {
            await axios.post(`${this.apiUrl}/v1/admin/tenants`, payload, { headers: this.getAuthHeaders() });
          } catch (e) {
            await axios.post(`${this.apiUrl}/v1/admin/tenant/create`, payload, { headers: this.getAuthHeaders() });
          }
          this.successMessage = "Tenant created successfully!";
        }
        this.closeModal();
        await this.fetchTenants();
      } catch (err) {
        console.error("Error saving tenant:", err);
        this.errorMessage = err.response?.data?.message || err.response?.data?.error || "Failed to save tenant. Please check required fields.";
      } finally {
        this.isSaving = false;
      }
    },
    async deleteTenant(id) {
      if (!confirm("Are you sure you want to delete this tenant?")) return;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        try {
          await axios.delete(`${this.apiUrl}/v1/admin/tenants/${id}`, { headers: this.getAuthHeaders() });
        } catch (e) {
          await axios.delete(`${this.apiUrl}/v1/admin/tenant/delete/${id}`, { headers: this.getAuthHeaders() });
        }
        this.successMessage = "Tenant deleted successfully!";
        await this.fetchTenants();
      } catch (err) {
        console.error("Error deleting tenant:", err);
        this.errorMessage = err.response?.data?.message || "Failed to delete tenant.";
      }
    }
  }
};
</script>
